import { useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { createBooking, getMeetingTypes } from '@/services/bookingService';
import { BOOKING_AREAS, BOOKING_CONSENT_WORDING } from '@/data/contact';
import { trackEvent } from '@/lib/analytics';
import { ProgressSteps } from './booking/ProgressSteps';
import { MeetingTypeStep } from './booking/MeetingTypeStep';
import { DateTimeStep } from './booking/DateTimeStep';
import { DetailsStep } from './booking/DetailsStep';
import { ReviewStep } from './booking/ReviewStep';
import { ConfirmStep } from './booking/ConfirmStep';
import { Reveal } from '../Reveal';

const EMPTY_DETAILS = {
  name: '',
  businessName: '',
  email: '',
  phone: '',
  location: '',
  areaOfInterest: '',
  context: '',
  consent: false,
  website: '', // honeypot
};

// Genuine product deep-link parameters recognised on the contact page.
const PRODUCT_NAMES = { 'amden-compass': 'Amden Compass', 'amden-pulse': 'Amden Pulse', 'amden-foresee': 'Foresee', 'amden-reach': 'Amden Reach' };

const utmFromLocation = () => {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((k) => {
    const v = params.get(k);
    if (v) utm[k] = v;
  });
  return Object.keys(utm).length ? utm : undefined;
};

// Section 4 — public appointment-booking journey. Five steps with a clear
// progress indicator; entered information is preserved when moving back.
// All availability and booking validation happens server-side.
export const BookingJourney = () => {
  const [meetingTypes, setMeetingTypes] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [step, setStep] = useState(1);
  const [meetingType, setMeetingType] = useState(null);
  const [slot, setSlot] = useState(null);
  const [details, setDetails] = useState(EMPTY_DETAILS);
  const [submitting, setSubmitting] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    getMeetingTypes()
      .then((res) => setMeetingTypes(res.meetingTypes || []))
      .catch(() => setLoadError(true));
  }, []);

  // Product deep-links (e.g. from /systems/compass or /systems/pulse):
  // prefill the area of interest and context, and preselect a meeting type
  // when one with a matching id is configured (?meeting=system-demonstration).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const product = PRODUCT_NAMES[params.get('interest')];
    if (product) {
      setDetails((d) => ({
        ...d,
        areaOfInterest: 'systems',
        context: d.context || `I would like to see a demonstration of ${product}.`,
      }));
      return;
    }
    // Service detail deep-links (e.g. /growth/website-support "Book a Consultation")
    const area = params.get('area');
    const service = params.get('service');
    if (area || service) {
      setDetails((d) => ({
        ...d,
        ...(area && BOOKING_AREAS.some((a) => a.value === area) ? { areaOfInterest: area } : {}),
        context: d.context || (service ? `I would like to discuss ${service.slice(0, 80)}.` : d.context),
      }));
    }
  }, []);

  useEffect(() => {
    if (!meetingTypes || meetingType) return;
    const wanted = new URLSearchParams(window.location.search).get('meeting');
    if (!wanted) return;
    const match = meetingTypes.find((t) => t.meetingTypeId === wanted);
    if (match) setMeetingType(match);
  }, [meetingTypes, meetingType]);

  const goTo = (n) => {
    setConflict(false);
    setStep(n);
  };

  const confirm = async () => {
    if (submitting) return; // duplicate-submission guard
    setSubmitting(true);
    setConflict(false);
    setSubmitError('');
    try {
      const res = await createBooking({
        meetingTypeId: meetingType.meetingTypeId,
        startUtc: slot.startUtc,
        name: details.name.trim(),
        businessName: details.businessName.trim(),
        email: details.email.trim(),
        phone: details.phone.trim() || undefined,
        location: details.location.trim() || undefined,
        areaOfInterest: details.areaOfInterest,
        context: details.context.trim() || undefined,
        meetingFormat: meetingType.format,
        consent: details.consent,
        consentWording: BOOKING_CONSENT_WORDING,
        pageUrl: window.location.href,
        utm: utmFromLocation(),
        website: details.website,
      });
      setResult(res); // confirmation only after genuine server-side storage
      trackEvent('booking_confirmed');
      setStep(5);
    } catch (err) {
      if (err?.response?.status === 409) {
        setConflict(true);
        setSlot(null);
        setStep(2);
      } else {
        const detail = err?.response?.data?.detail;
        setSubmitError(
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail) && detail[0]?.msg
              ? `Please check your details: ${detail[0].msg}`
              : 'Something went wrong while confirming your appointment. Please check your details and try again.'
        );
        setStep(4);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="book-appointment" aria-labelledby="booking-heading" data-testid="book-appointment" className="scroll-mt-28 bg-mist py-16 sm:py-24">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <Reveal>
          <p className="flex items-center gap-3">
            <span className="font-mono text-xs font-medium tracking-widest text-gold-700">04</span>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-700">Booking</span>
          </p>
          <h2 id="booking-heading" data-testid="booking-heading" className="mt-4 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
            Book an appointment
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Choose an available time for an initial conversation about your business and where you
            may need support.
          </p>

          <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 sm:p-10">
            {meetingTypes === null && !loadError ? (
              <div className="flex items-center justify-center py-16" role="status" data-testid="booking-loading">
                <Loader2 className="h-6 w-6 animate-spin text-navy-900" aria-hidden="true" />
                <span className="sr-only">Loading booking options</span>
              </div>
            ) : loadError ? (
              <div className="py-12 text-center" role="alert" data-testid="booking-load-error">
                <p className="inline-flex items-center gap-2 text-sm font-semibold text-red-700">
                  <AlertCircle className="h-4 w-4" aria-hidden="true" />
                  Booking options could not be loaded. Please refresh the page or send an enquiry instead.
                </p>
              </div>
            ) : meetingTypes.length === 0 ? (
              <div className="py-12 text-center" data-testid="booking-no-types">
                <p className="text-lg font-extrabold text-navy-900">
                  Online appointment times are currently being updated
                </p>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-600">
                  Please send an enquiry and we’ll arrange a suitable time.
                </p>
              </div>
            ) : (
              <>
                <ProgressSteps current={step} />
                {step === 1 && (
                  <MeetingTypeStep
                    meetingTypes={meetingTypes}
                    selected={meetingType}
                    onSelect={(mt) => {
                      setMeetingType(mt);
                      trackEvent('meeting_type_selected');
                    }}
                    onNext={() => {
                      trackEvent('booking_started');
                      goTo(2);
                    }}
                  />
                )}
                {step === 2 && meetingType && (
                  <DateTimeStep
                    meetingType={meetingType}
                    slot={slot}
                    onSelectSlot={setSlot}
                    onBack={() => goTo(1)}
                    onNext={() => goTo(3)}
                  />
                )}
                {step === 3 && (
                  <DetailsStep details={details} onChange={setDetails} onBack={() => goTo(2)} onNext={() => goTo(4)} />
                )}
                {step === 4 && meetingType && slot && (
                  <ReviewStep
                    meetingType={meetingType}
                    slot={slot}
                    details={details}
                    onEdit={goTo}
                    onBack={() => goTo(3)}
                    onConfirm={confirm}
                    submitting={submitting}
                    conflict={conflict}
                    submitError={submitError}
                  />
                )}
                {step === 5 && result && <ConfirmStep result={result} />}
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
